<script module>
	export { RecentreMap, ToggleWikiBtn };
</script>

<script lang="ts">
	import { browser } from '$app/environment';
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import type { WikiMarker } from '$lib/api/wiki/response.types';
	import { wikiGeoSearch, wikiGetBatchSummaries, wikiGetWikiDetails } from '$lib/api/wiki/wiki.api';
	import { swipeClose } from '$lib/attachments/swipeClose';
	import WikiMapIcon from '$lib/components/ui/map/WikiMapIcon.svelte';
	import { ViewStateController } from '$lib/controllers/lastView/viewState.controller';
	import { flattenCoordinates, type Coordinates } from '$lib/features/_other/coordinates.type';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';
	import { ANALYTICS_EVENT_NAMES, TAILWIND_BREAKPOINT, WIKI_POINTS_SETTINGS } from '$lib/statics';
	import { getStreetViewUrl } from '$lib/utils/geo.util';
	import { useBackButtonInterception } from '$lib/utils/history.util.svelte';
	import { Map as LibreMap, LngLat, type LngLatBounds } from 'maplibre-gl';
	import { watch } from 'runed';
	import Supercluster, { type ClusterFeature, type ClusterProperties, type PointFeature } from 'supercluster';
	import { onMount, untrack } from 'svelte';
	import { Marker } from 'svelte-maplibre-gl';
	import type { Attachment } from 'svelte/attachments';
	import { SvelteMap, SvelteSet } from 'svelte/reactivity';
	import { fly } from 'svelte/transition';
	import IconChevronLeft from '~icons/mdi/chevron-left';
	import IconChevronRight from '~icons/mdi/chevron-right';
	import IconClose from '~icons/mdi/close';
	import IconStreetView from '~icons/mdi/google-street-view';
	import IconImageFilterCenterFocus from '~icons/mdi/image-filter-center-focus';
	import IconReadMore from '~icons/mdi/read-more';
	import IconWikipedia from '~icons/mdi/wikipedia';

	interface CenterPointOptions {
		center: Coordinates;
		zoom: number;
		pitch?: number;
		bearing?: number;
	}

	interface CenterBoundsOptions {
		bounds: LngLatBounds;
		maxBounds?: LngLatBounds;
		padding?: number;
		pitch?: number;
	}

	interface PoiDetails {
		name: string;
		coordinates: Coordinates;
		category: string;
		images: string[];
		description: string | null;
	}

	/** Everything we know about an accepted (has-an-image) article. */
	interface CachedWiki {
		marker: WikiMarker;
		image: string;
		pageViews: number;
		description: string | null;
		subtitle: string | null;
	}

	type ClusterItem = ClusterFeature<ClusterProperties> | PointFeature<WikiMarker>;

	const {
		map,
		parentViewStateController,
		onLoadingPagesChange
	}: {
		map: LibreMap | undefined;
		parentViewStateController?: ViewStateController | undefined;
		onLoadingPagesChange: (isLoading: boolean) => void;
	} = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();

	const MIN_ZOOM_FOR_POIS: number = 6.5;
	const MAX_CACHE_SIZE: number = 1000;
	const AREA_COVERED_METERS: number = 300; // don't re-search within this distance of an earlier successful search
	const MAX_SEARCHED_CENTERS: number = 300;
	const BATCH_SIZE: number = 50; // Wikipedia's per-request title limit

	const index = new Supercluster({ radius: 40, maxZoom: 15 });

	const cache = new SvelteMap<string, CachedWiki>();
	const ignored = new Set<string>(); // articles with no image: never fetched or shown again
	const detailsCache = new Map<string, PoiDetails>(); // full panel details
	const searchedCenters: LngLat[] = []; // areas already searched successfully
	let fetchQueued = false;

	let clusters = $state<ClusterItem[]>([]);
	let activePoi = $state<PoiDetails | null>(null);
	let activeImageIndex = $state<number>(0);
	let loadingPoiId = $state<string | null>(null);
	let isLoadingPages = $state<boolean>(false);
	let zoom = $state<number>(untrack(() => map?.getZoom() ?? 15));

	const viewStateController = $derived<ViewStateController | null>(
		parentViewStateController !== undefined ? parentViewStateController : map !== undefined ? new ViewStateController(map) : null
	);
	const activeWikiMarkers = $derived<WikiMarker[]>(zoom >= MIN_ZOOM_FOR_POIS ? Array.from(cache.values(), (c) => c.marker) : []);
	const popularTitles = $derived.by<SvelteSet<string>>(() => {
		const minArticlesTreshold = 3;
		if (minArticlesTreshold < 3) return new SvelteSet();
		const viewCounts = Array.from(cache.values(), (c) => c.pageViews).sort((a, b) => a - b);
		const threshold = viewCounts[Math.floor(viewCounts.length * WIKI_POINTS_SETTINGS.topArticlesPercentile)];
		if (threshold <= 0) return new SvelteSet();

		const titles = new SvelteSet<string>();
		for (const [title, c] of cache) {
			if (c.pageViews >= threshold) titles.add(title);
		}
		return titles;
	});

	async function fetchViewportWikiPages(): Promise<void> {
		if (zoom < MIN_ZOOM_FOR_POIS || map === undefined) return;

		if (isLoadingPages) {
			fetchQueued = true; // re-check once the current request finishes, using the then-current map centre
			return;
		}

		const center = map.getCenter();
		if (isAreaCovered(center)) return;

		isLoadingPages = true;
		try {
			const knownTitles = new Set<string>([...cache.keys(), ...ignored]);
			const results = await wikiGeoSearch({
				centre: { lat: center.lat, lon: center.lng },
				cachedWikiItemTitles: knownTitles
			});

			const fresh = new Map<string, WikiMarker>();
			for (const r of results) {
				if (!cache.has(r.title) && !ignored.has(r.title)) fresh.set(r.title, r);
			}

			if (fresh.size > 0) {
				const summaries = await fetchSummaries([...fresh.keys()]);

				for (const [title, marker] of fresh) {
					const summary = summaries.get(title);
					const image = marker.image || summary?.thumbnail || null;

					if (!image) {
						ignored.add(title);
						continue;
					}

					if (cache.size >= MAX_CACHE_SIZE) {
						const oldest = cache.keys().next().value;
						if (oldest !== undefined) cache.delete(oldest);
					}

					cache.set(title, {
						marker: { ...marker, image },
						image,
						pageViews: summary?.pageViews ?? 0,
						description: summary?.description ?? null,
						subtitle: summary?.subtitle ?? null
					});
				}
			}

			// Only mark the area as searched once everything succeeded, so failures can retry
			searchedCenters.push(center);
			if (searchedCenters.length > MAX_SEARCHED_CENTERS) searchedCenters.shift();
		} catch (err) {
			console.warn('Failed to fetch viewport Wikipedia pages:', err);
		} finally {
			isLoadingPages = false;
			if (fetchQueued) {
				fetchQueued = false;
				void fetchViewportWikiPages();
			}
		}
	}

	function isAreaCovered(center: LngLat): boolean {
		return searchedCenters.some((c) => c.distanceTo(center) < AREA_COVERED_METERS);
	}

	async function fetchSummaries(titles: string[]) {
		const chunks: string[][] = [];
		for (let i = 0; i < titles.length; i += BATCH_SIZE) chunks.push(titles.slice(i, i + BATCH_SIZE));
		const results = await Promise.all(chunks.map((chunk) => wikiGetBatchSummaries(chunk, { imageSize: 250 })));
		return new Map(results.flatMap((m) => [...m]));
	}

	async function handleWikiMarkerClick(title: string) {
		const entry = cache.get(title);
		if (!map || !entry) return;
		if (activePoi?.name === title || loadingPoiId === title) return;

		loadingPoiId = title;
		try {
			let poi = detailsCache.get(title);
			if (!poi) {
				const details = await wikiGetWikiDetails(title, { imageSize: 960 });
				poi = {
					name: details.title,
					coordinates: entry.marker.coordinates,
					category: details.subtitle || entry.subtitle || 'landmark',
					images: details.images.length > 0 ? details.images : [entry.image],
					description: details.description ?? entry.description
				};
				detailsCache.set(title, poi);
			}

			const clickedOnSomethingElse = loadingPoiId !== title;
			if (clickedOnSomethingElse) return;

			viewStateController?.add();
			activePoi = poi;
			activeImageIndex = 0;

			map.flyTo({
				center: { lng: poi.coordinates.longitude, lat: poi.coordinates.latitude },
				zoom: 16,
				duration: 1800,
				essential: true
			});
		} catch (err) {
			console.warn(`Failed to load Wikipedia details for '${title}':`, err);
		} finally {
			if (loadingPoiId === title) loadingPoiId = null;
		}
	}

	function updateClusters() {
		if (!map) return;
		const bounds = map.getBounds();
		const clusterZoom = Math.floor(map.getZoom());
		const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()] as [number, number, number, number];
		clusters = index.getClusters(bbox, clusterZoom) as ClusterItem[];
	}

	onMount(() => {
		void fetchViewportWikiPages();
	});

	$effect(() => {
		onLoadingPagesChange(isLoadingPages);
	});

	$effect(() => {
		const points = activeWikiMarkers.map((m) => ({
			type: 'Feature' as const,
			properties: m,
			geometry: {
				type: 'Point' as const,
				coordinates: flattenCoordinates(m.coordinates)
			}
		}));

		index.load(points);
		updateClusters();
	});

	$effect(() => {
		if (map === undefined) return;
		let settleTimer: ReturnType<typeof setTimeout> | undefined;
		const SETTLE_DELAY = 200; // ms of quiet before we trust the move is really done (another flyTo() may follow)

		const handleMoveEnd = () => {
			updateClusters();
			if (settleTimer) clearTimeout(settleTimer);
			settleTimer = setTimeout(() => {
				if (!map.isMoving()) void fetchViewportWikiPages();
			}, SETTLE_DELAY);
		};
		const handleMoveStart = () => {
			if (settleTimer) clearTimeout(settleTimer);
		};
		const handleZoomEnd = () => (zoom = map.getZoom()); // named, so map.off() actually removes it

		map.on('moveend', handleMoveEnd);
		map.on('movestart', handleMoveStart);
		map.on('zoomend', handleZoomEnd);

		const canvas = map.getCanvasContainer();
		const stopPropagation = (e: TouchEvent) => e.stopPropagation();
		canvas.addEventListener('touchstart', stopPropagation, { passive: true });
		canvas.addEventListener('touchmove', stopPropagation, { passive: true });
		canvas.addEventListener('touchend', stopPropagation, { passive: true });

		return () => {
			if (settleTimer) clearTimeout(settleTimer);
			map.off('moveend', handleMoveEnd);
			map.off('movestart', handleMoveStart);
			map.off('zoomend', handleZoomEnd);
			canvas.removeEventListener('touchstart', stopPropagation);
			canvas.removeEventListener('touchmove', stopPropagation);
			canvas.removeEventListener('touchend', stopPropagation);
		};
	});

	watch(
		() => activePoi,
		(curr, prev) => {
			const poiWasAndIsNull = curr === null && (prev === null || prev === undefined);
			if (curr !== null) {
				const isPopular: boolean = popularTitles.has(curr.name);
				analyticsApi.sendAnalytics({
					event: ANALYTICS_EVENT_NAMES.wikiMapPoi,
					payload: { name: curr.name, popular: isPopular }
				});
			}
			if (poiWasAndIsNull) return; // in case the wiki button gets disabled and enabled, don't restore the (initial) view state
			if (activePoi === null && map) {
				viewStateController?.restore();
			}
		}
	);

	useBackButtonInterception({
		stateKey: 'wikiActivePoi',
		active: () => activePoi !== null,
		enabled: () => deviceScreen.smallerThanDesktop,
		getData: () => activePoi,
		onBack: () => (activePoi = null),
		onForward: (poi) => {
			if (poi !== null) {
				handleWikiMarkerClick(poi.name);
			}
		}
	});

	const portal: Attachment = (node) => {
		if (browser && typeof window !== 'undefined') {
			// Append directly to the body to break out of the local stacking context
			document.body.appendChild(node);
		}

		return () => {
			if (browser && node.parentNode) {
				node.remove();
			}
		};
	};
</script>

{#each clusters as cluster (cluster.id || (cluster.properties as WikiMarker).title)}
	{const isCluster = $derived<boolean>('cluster' in cluster.properties && !!cluster.properties.cluster)}

	{#if isCluster}
		{const props = $derived<ClusterProperties>(cluster.properties as ClusterProperties)}
		<Marker lnglat={{ lng: cluster.geometry.coordinates[0], lat: cluster.geometry.coordinates[1] }}>
			{#snippet content()}
				<button
					class="relative flex size-8 cursor-pointer items-center justify-center rounded-field border-2 border-accent-content bg-accent text-xs font-black text-accent-content transition-all hover:scale-110 active:scale-95 md:size-15 lg:size-10"
					type="button"
					onclick={(e) => {
						e.stopPropagation();
						const expansionZoom = index.getClusterExpansionZoom(props.cluster_id);
						map?.easeTo({
							center: { lng: cluster.geometry.coordinates[0], lat: cluster.geometry.coordinates[1] },
							zoom: Math.min(17, expansionZoom + 0.5),
							essential: true
						});
					}}
				>
					{props.point_count_abbreviated || props.point_count}
				</button>
			{/snippet}
		</Marker>
	{:else}
		{const wiki = $derived<WikiMarker>(cluster.properties as WikiMarker)}
		{#if wiki.image}
			<WikiMapIcon
				wiki={{ title: wiki.title, imageUrl: wiki.image, coords: wiki.coordinates, isPopular: popularTitles.has(wiki.title) }}
				shape="square"
				isActive={activePoi?.name === wiki.title}
				isLoading={loadingPoiId === wiki.title}
				onclick={() => handleWikiMarkerClick(wiki.title)}
			/>
		{/if}
	{/if}
{/each}

{#if activePoi}
	<div
		{@attach portal}
		{@attach swipeClose({ onClose: () => (activePoi = null), enabled: deviceScreen.isMobile })}
		class="fixed right-0 bottom-0 left-0 z-120 flex h-[80vh] max-h-[80vh] w-full flex-col overflow-hidden overscroll-contain rounded-t-box border border-base-200/80 bg-base-200 text-base-content shadow-2xl transition-transform duration-300 md:fixed md:top-4 md:right-4 md:bottom-4 md:left-auto md:h-[calc(100vh-2rem)] md:max-h-none md:w-[35vw] md:rounded-box lg:w-[22vw]"
		role="dialog"
		aria-modal="false"
		aria-label="POI Details"
		tabindex="-1"
		onwheel={(e) => e.stopPropagation()}
		ontouchstart={(e) => e.stopPropagation()}
		ontouchmove={(e) => e.stopPropagation()}
		ontouchend={(e) => e.stopPropagation()}
		in:fly={{
			x: deviceScreen.isMobile ? 0 : 20,
			y: deviceScreen.isMobile ? 100 : 0,
			duration: 300
		}}
		out:fly={{ y: deviceScreen.isMobile ? 100 : 0, x: deviceScreen.isMobile ? 0 : 20, duration: 200 }}
	>
		{@render PanelContent(activePoi!, () => (activePoi = null))}
	</div>
{/if}

{#snippet PanelContent(poi: PoiDetails, onClose: () => void)}
	<div class="flex shrink-0 items-end justify-end border-b border-base-content/5 p-2">
		<button class="btn btn-circle btn-ghost btn-sm" onclick={onClose} aria-label="Close panel">
			<IconClose class="text-lg" />
		</button>
	</div>

	<div class="mt-2 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden overscroll-contain px-4">
		<div class="flex shrink-0 flex-col">
			<h3 class="text-xl leading-tight font-bold text-base-content">{poi.name}</h3>
			<i class="text-xs leading-relaxed font-light text-base-content/70 capitalize">
				{poi.category}
			</i>
		</div>

		{#if poi.images.length > 0}
			<div class="h-[50%] w-full shrink-0 overflow-hidden rounded-box">
				{@render Images()}
			</div>
		{/if}

		{#if poi.description}
			<div class="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1 pr-1">
				<p class="text-sm leading-relaxed font-light text-base-content/85">
					{poi.description}
				</p>
			</div>
		{/if}
	</div>

	<div class="flex shrink-0 flex-col items-center gap-2 border-t border-base-content/5 p-4">
		{@render Footer()}
	</div>

	{#snippet Images()}
		<div class="relative h-full w-full overflow-hidden bg-base-200">
			{#each poi.images as src, i (`${i}:${src}`)}
				<img
					class={{
						'absolute inset-0 h-full w-full object-contain transition-opacity duration-150': true,
						'opacity-100': i === activeImageIndex,
						'opacity-0': i !== activeImageIndex
					}}
					{src}
					alt={`${poi.name} - ${i + 1}`}
					loading="eager"
					decoding={i === 0 ? 'sync' : 'async'}
					fetchpriority={i === 0 ? 'high' : 'low'}
					draggable="false"
				/>
			{/each}

			{#if poi.images.length > 1}
				<button
					class="btn absolute top-1/2 left-2 z-10 btn-circle -translate-y-1/2 border-none bg-black/60 text-white btn-xs hover:bg-black/80"
					onclick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						activeImageIndex = (activeImageIndex - 1 + poi.images.length) % poi.images.length;
					}}
					aria-label="Previous image"
				>
					<IconChevronLeft />
				</button>
				<button
					class="btn absolute top-1/2 right-2 z-10 btn-circle -translate-y-1/2 border-none bg-black/60 text-white btn-xs hover:bg-black/80"
					onclick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						activeImageIndex = (activeImageIndex + 1) % poi.images.length;
					}}
					aria-label="Next image"
				>
					<IconChevronRight />
				</button>
				<div
					class="pointer-events-none absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1 rounded-full bg-black/30 px-2 py-0.5"
				>
					{#each poi.images as _, idx (idx)}
						<span class="h-1 rounded-full transition-all {idx === activeImageIndex ? 'w-2 bg-white' : 'w-1 bg-white/50'}"></span>
					{/each}
				</div>
			{/if}
		</div>
	{/snippet}

	{#snippet Footer()}
		<div class="flex w-full flex-row justify-center gap-2">
			<a
				class="btn flex flex-1 items-center justify-center gap-1 rounded-full btn-primary btn-sm"
				href={`https://en.wikipedia.org/wiki/${encodeURIComponent(poi.name)}`}
				target="_blank"
				rel="noopener noreferrer"
			>
				<IconReadMore />
				Read Article
			</a>
			<a
				class="flex flex-1 link items-center justify-center gap-1 rounded-full link-hover"
				href={getStreetViewUrl({ latitude: poi.coordinates.latitude, longitude: poi.coordinates.longitude })}
				target="_blank"
				rel="noopener noreferrer"
			>
				<IconStreetView />
				Street View
			</a>
		</div>

		<span class="text-center text-xs tracking-wide text-base-content/70 select-none">
			Text and media from Wikipedia under CC-BY-SA
		</span>
	{/snippet}
{/snippet}

{#snippet RecentreMap(map: LibreMap, options: CenterPointOptions | CenterBoundsOptions)}
	<button
		type="button"
		// forcing grid! and place-items-center! to override the maplibre defaults to make sure the button is centered in Firefox
		class="btn grid! btn-square h-full w-full place-items-center! rounded-sm border-none bg-transparent p-0 text-black btn-sm hover:bg-base-200/50"
		onclick={() => {
			map.stop();

			function isBoundsOptions(options: CenterPointOptions | CenterBoundsOptions): options is CenterBoundsOptions {
				return 'bounds' in options;
			}

			if (isBoundsOptions(options)) {
				if (options.maxBounds) map.setMaxBounds(options.maxBounds);
				map.fitBounds(options.bounds, {
					padding: options.padding ?? 45,
					pitch: options.pitch ?? 0,
					bearing: 0,
					duration: 1200,
					essential: true
				});
			} else {
				map.flyTo({
					center: { lng: options.center.longitude, lat: options.center.latitude },
					zoom: options.zoom,
					pitch: options.pitch ?? 45,
					bearing: options.bearing ?? 0,
					duration: 1200,
					essential: true
				});
			}
		}}
		aria-label="Recenter Map"
		title="Recenter Map"
	>
		<IconImageFilterCenterFocus class="text-lg" />
	</button>
{/snippet}

{#snippet ToggleWikiBtn({
	isEnabled,
	isLoadingPages,
	onToggle
}: {
	isEnabled: boolean;
	isLoadingPages: boolean;
	onToggle: (isEnabled: boolean) => void;
})}
	<button
		type="button"
		class={{
			'btn grid! btn-square h-full w-full place-items-center! rounded-sm border-none bg-transparent p-0 transition-colors btn-sm hover:bg-base-200/50': true,
			'text-black': isEnabled,
			'text-black/50': !isEnabled
		}}
		onclick={() => onToggle(!isEnabled)}
		aria-label={isEnabled ? 'Hide Wikipedia Landmarks' : 'Explore Nearby with Wikipedia'}
		title={isEnabled ? 'Hide Wikipedia Landmarks' : 'Explore Nearby with Wikipedia'}
	>
		{#if isLoadingPages}
			<span class="loading loading-sm loading-spinner"></span>
		{:else}
			<IconWikipedia class="text-lg" />
		{/if}
	</button>
{/snippet}
